import { createReducer } from '@reduxjs/toolkit';
import { setSelectedOrganizationAction } from '@redux/organization/organizationActions';
import { defaultActionFail, defaultActionStart, getActionStringWithoutState, setDefaultFail, setDefaultStart } from '@redux/reduxUtils';
import {
  cancelTaskAction,
  clearGeneratedTaskMessageAction,
  clearGeneratedTaskNameAction,
  clearGeneratedTaskSummaryAction,
  clearSelectedAssessment,
  clearSelectedTaskDetails,
  generateTaskMessageAction,
  getAssessmentsListAction,
  getEngagementPlansAction,
  getPatientEngagementPlansAction,
  getPersonUpcomingTasksAction,
  getPersonTasksByDateRangeAction,
  getTaskDetailsAction,
  getTasksListAction,
  postNewTaskAction,
  setSelectedAssessment,
  TASK_ACTION_PREFIX,
  generateAssessmentMessageAction,
  setPreviewAssessmentId,
  getPreviewAssessmentDetailsAction,
  clearPreviewAssessmentDetails,
  getAssessmentResultAction,
  disableEngagementPlanAction,
  createEngagementPlanAction,
  clearUpdatingEngagementPlanAction,
  duplicateEngagementPlanAction,
  updateEngagementPlansListAction
} from '@redux/tasks/tasksActions';
import { TasksState } from '@redux/tasks/tasksTypes';

export const initialTasksState: TasksState = {
  isLoading: {},
  error: {},
  success: {},
  tasksList: [],
  selectedTaskDetails: null,
  personUpcomingTasks: [],
  personTasksByDateRangeTasks: [],
  generatedTaskMessage: null,
  generatedTaskName: null,
  generatedTaskSummary: null,
  engagementPlans: [],
  patientEngagementPlans: [],
  updatingEngagementPlan: null,
  assessmentsList: null,
  selectedAssessment: null,
  previewAssessmentId: null,
  previewAssessmentDetails: null
};

export default createReducer(initialTasksState, (builder) => {
  builder
    .addCase(setSelectedOrganizationAction, () => {
      // Clear tasks data when switching organizations to prevent data leakage
      return initialTasksState;
    })
    .addCase(getTasksListAction.start, defaultActionStart)
    .addCase(getTasksListAction.fail, defaultActionFail)
    .addCase(getTasksListAction.success, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.isLoading[tasksTag] = false;
      state.tasksList = action.payload ?? [];
    })

    .addCase(getTaskDetailsAction.start, defaultActionStart)
    .addCase(getTaskDetailsAction.fail, defaultActionFail)
    .addCase(getTaskDetailsAction.success, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.isLoading[tasksTag] = false;
      state.selectedTaskDetails = action.payload;
    })

    .addCase(getPersonUpcomingTasksAction.start, defaultActionStart)
    .addCase(getPersonUpcomingTasksAction.fail, defaultActionFail)
    .addCase(getPersonUpcomingTasksAction.success, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.isLoading[tasksTag] = false;
      state.personUpcomingTasks = action.payload;
    })

    .addCase(getPersonTasksByDateRangeAction.start, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.isLoading[tasksTag] = true;
      delete state.error[tasksTag];
      // Clear existing data when starting a new request
      state.personTasksByDateRangeTasks = [];
    })
    .addCase(getPersonTasksByDateRangeAction.fail, defaultActionFail)
    .addCase(getPersonTasksByDateRangeAction.success, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.isLoading[tasksTag] = false;
      state.personTasksByDateRangeTasks = action.payload;
    })

    .addCase(cancelTaskAction.start, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.isLoading[tasksTag] = true;
      delete state.error[tasksTag];
      delete state.success[cancelTaskAction.success.type];
    })
    .addCase(cancelTaskAction.fail, defaultActionFail)
    .addCase(cancelTaskAction.success, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.isLoading[tasksTag] = false;
      state.success[action.type] = true;
      delete state.error[tasksTag];
      const updatedTasksList = [...state.tasksList];
      const canceledTaskIndex = state.tasksList.findIndex((task) => task.taskId === action.payload?.id);
      if (canceledTaskIndex >= 0) {
        updatedTasksList[canceledTaskIndex].status = action.payload.status;
        state.tasksList = updatedTasksList;
      }
    })
    .addCase(postNewTaskAction.start, defaultActionStart)
    .addCase(postNewTaskAction.fail, defaultActionFail)
    .addCase(postNewTaskAction.success, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.isLoading[tasksTag] = false;
    })
    .addCase(generateTaskMessageAction.start, defaultActionStart)
    .addCase(generateTaskMessageAction.fail, defaultActionFail)
    .addCase(generateTaskMessageAction.success, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.generatedTaskMessage = action.payload.generatedMessage;
      state.generatedTaskName = action.payload.taskName;
      state.generatedTaskSummary = action.payload.taskSummary || null;
      state.isLoading[tasksTag] = false;
    })
    .addCase(clearGeneratedTaskMessageAction, (state) => {
      state.generatedTaskMessage = null;
    })
    .addCase(clearGeneratedTaskNameAction, (state) => {
      state.generatedTaskName = null;
    })
    .addCase(clearGeneratedTaskSummaryAction, (state) => {
      state.generatedTaskSummary = null;
    })
    .addCase(clearSelectedTaskDetails, (state) => {
      state.selectedTaskDetails = null;
    })
    .addCase(getEngagementPlansAction.start, defaultActionStart)
    .addCase(getEngagementPlansAction.fail, defaultActionFail)
    .addCase(getEngagementPlansAction.success, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.isLoading[tasksTag] = false;
      state.engagementPlans = action.payload;
    })

    .addCase(getPatientEngagementPlansAction.start, defaultActionStart)
    .addCase(getPatientEngagementPlansAction.fail, defaultActionFail)
    .addCase(getPatientEngagementPlansAction.success, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.isLoading[tasksTag] = false;
      state.patientEngagementPlans = action.payload;
    })

    .addCase(disableEngagementPlanAction.start, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.isLoading[tasksTag] = true;
      delete state.error[tasksTag];
      delete state.success[disableEngagementPlanAction.success.type];
    })
    .addCase(disableEngagementPlanAction.fail, defaultActionFail)
    .addCase(disableEngagementPlanAction.success, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.isLoading[tasksTag] = false;
      state.success[action.type] = true;
      delete state.error[tasksTag];
    })

    .addCase(createEngagementPlanAction.start, defaultActionStart)
    .addCase(createEngagementPlanAction.fail, defaultActionFail)
    .addCase(createEngagementPlanAction.success, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.updatingEngagementPlan = null;
      state.isLoading[tasksTag] = false;
    })

    .addCase(updateEngagementPlansListAction, (state, action) => {
      if (action.payload.add) {
        state.engagementPlans = [...state.engagementPlans, action.payload.plan];
      } else {
        state.engagementPlans = state.engagementPlans.filter((plan) => plan.id !== action.payload.plan.id);
      }
    })

    .addCase(duplicateEngagementPlanAction, (state, action) => {
      state.updatingEngagementPlan = action.payload;
    })

    .addCase(clearUpdatingEngagementPlanAction, (state) => {
      state.updatingEngagementPlan = null;
    })

    .addCase(getAssessmentsListAction.start, defaultActionStart)
    .addCase(getAssessmentsListAction.fail, defaultActionFail)
    .addCase(getAssessmentsListAction.success, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.isLoading[tasksTag] = false;
      state.assessmentsList = action.payload ?? [];
    })

    .addCase(setSelectedAssessment, (state, action) => {
      state.selectedAssessment = action.payload ?? null;
    })

    .addCase(clearSelectedAssessment, (state) => {
      state.selectedAssessment = null;
    })

    .addCase(generateAssessmentMessageAction.start, defaultActionStart)
    .addCase(generateAssessmentMessageAction.fail, defaultActionFail)
    .addCase(generateAssessmentMessageAction.success, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.generatedTaskMessage = action.payload.startingMessage;
      state.isLoading[tasksTag] = false;
    })

    .addCase(setPreviewAssessmentId, (state, action) => {
      state.previewAssessmentId = action.payload ?? null;
    })

    .addCase(getPreviewAssessmentDetailsAction.start, defaultActionStart)
    .addCase(getPreviewAssessmentDetailsAction.fail, defaultActionFail)
    .addCase(getPreviewAssessmentDetailsAction.success, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.previewAssessmentDetails = action.payload;
      state.isLoading[tasksTag] = false;
    })

    .addCase(clearPreviewAssessmentDetails, (state) => {
      state.previewAssessmentDetails = null;
      state.previewAssessmentId = null;
    })

    .addCase(getAssessmentResultAction.start, defaultActionStart)
    .addCase(getAssessmentResultAction.fail, defaultActionFail)
    .addCase(getAssessmentResultAction.success, (state, action) => {
      const tasksTag = getActionStringWithoutState(action.type);
      state.previewAssessmentDetails = action.payload;
      state.isLoading[tasksTag] = false;
    });

  builder
    .addMatcher(
      // matcher can be defined inline as a type predicate function
      (action): any => action.type.endsWith('-start') && action.type.startsWith(TASK_ACTION_PREFIX),
      (state, action) => {
        setDefaultStart(state, action.type);
      }
    )
    .addMatcher(
      // matcher can be defined inline as a type predicate function
      (action): any => action.type.endsWith('-success') && action.type.startsWith(TASK_ACTION_PREFIX),
      (state, action) => {
        const tag = getActionStringWithoutState(action.type);
        state.isLoading[tag] = false;
      }
    )
    .addMatcher(
      // matcher can be defined inline as a type predicate function
      (action): any => action.type.endsWith('-fail') && action.type.startsWith(TASK_ACTION_PREFIX),
      (state, action) => {
        setDefaultFail(state, action);
      }
    );
});
